import React from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  makeStyles,
  Theme,
  Typography,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
  DialogTitle,
  DialogContent,
} from '@material-ui/core';
import { XCircle } from 'react-feather';
import clsx from 'clsx';
import { ModalWidthValues, BottomTransition } from '../helpers';
import { colors } from '../../../theme';

type DialogueProps = {
  id?: string;
  title?: React.ReactNode;
  preventMobileFullscreen?: boolean;
  children?: React.ReactNode;
  open: boolean;
  onCloseRequested?: () => void;
  maxWidth?: ModalWidthValues;
  fullWidth?: boolean;
  wrapperStyles?: string;
};

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    backgroundColor: colors.madison,
    [theme.breakpoints.down('sm')]: {
      backgroundColor: (useFullScreen: boolean): string => {
        return useFullScreen ? colors.white : colors.madison;
      },
    },
  },
  dynamicTextColor: {
    color: colors.white,
    [theme.breakpoints.down('sm')]: {
      color: (useFullScreen: boolean): string => {
        return useFullScreen ? colors.madison : colors.white;
      },
    },
  },
  appBarOffset: theme.mixins.toolbar,
  closeIcon: {
    position: 'absolute',
    top: '50%',
    marginTop: -12,
    right: theme.spacing(2),
  },
  contentWrapping: {
    padding: theme.spacing(2),
  },
  paddedText: {
    display: 'inline-block',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    fontSize: 16,
  },
  dialoguePaper: {
    background: '#f9f9f9',
    position: 'relative',
    borderRadius: 10,
    [theme.breakpoints.down('sm')]: {
      borderRadius: (useFullScreen: boolean): number =>
        useFullScreen ? 0 : 10,
    },
  },
}));

const Modal = ({
  id,
  title,
  open,
  onCloseRequested,
  children,
  preventMobileFullscreen = false,
  maxWidth = 'xs',
  fullWidth,
  wrapperStyles,
}: DialogueProps): JSX.Element => {
  const useFullScreen = !preventMobileFullscreen;
  const classes = useStyles(useFullScreen);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      id={id}
      open={open}
      fullScreen={isMobile && useFullScreen}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        className: classes.dialoguePaper,
      }}
      onClose={onCloseRequested}
      TransitionComponent={BottomTransition}
    >
      {title && (
        <>
          <DialogTitle className={classes.appBarOffset}>
            <AppBar
              data-testid="dialogue-appbar"
              position="absolute"
              className={classes.appBar}
              elevation={0}
            >
              <Toolbar>
                <Grid container justifyContent="center">
                  {typeof title === 'string' ? (
                    <Typography
                      data-testid="title-typography"
                      variant="body1"
                      className={classes.dynamicTextColor}
                    >
                      {title}
                    </Typography>
                  ) : (
                    title
                  )}
                </Grid>
              </Toolbar>
              {onCloseRequested && (
                <IconButton
                  data-testid="close-button"
                  className={classes.closeIcon}
                  onClick={onCloseRequested}
                >
                  <XCircle className={classes.dynamicTextColor} />
                </IconButton>
              )}
            </AppBar>
          </DialogTitle>
        </>
      )}
      <DialogContent
        className={clsx(classes.contentWrapping, wrapperStyles)}
        data-testid="content-wrapper"
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
